import { useState, useEffect, useCallback } from 'react';
import { useWeeklyFolders } from '@/contexts/WeeklyFolderContext';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import styles from './DocumentsPage.module.css';

interface Document {
  id: number;
  original_name: string;
  filename: string;
  mimetype: string;
  size: number;
  folder_type: 'weekly' | 'permanent';
  weekly_folder_id: string | null;
  status: 'processing' | 'completed' | 'error' | 'pending' | 'needs_review';
  analysis: string | null;
  tags: string[];
  error_message: string | null;
  parent_document_id: number | null;
  page_number: number | null;
  created_at: string;
}

interface ChildPage extends Document {
  page_number: number;
}

type FolderType = 'weekly' | 'permanent';

export function DocumentsPage() {
  const { selectedFolder } = useWeeklyFolders();
  const { isAdmin } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [folderType, setFolderType] = useState<FolderType>('weekly');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Page upload state
  const [pageUploadDoc, setPageUploadDoc] = useState<Document | null>(null);
  const [childPages, setChildPages] = useState<ChildPage[]>([]);
  const [isUploadingPages, setIsUploadingPages] = useState(false);
  const [isConsolidating, setIsConsolidating] = useState(false);
  const [pageUploadProgress, setPageUploadProgress] = useState(0);

  const loadDocuments = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let url = `/documents?folder_type=${folderType}`;
      if (folderType === 'weekly' && selectedFolder) {
        url += `&weekly_folder_id=${selectedFolder.id}`;
      }

      const response = await api.get<Document[]>(url);
      if (response.success && response.data) {
        setDocuments(response.data);
      } else {
        setError(response.error || 'Failed to load documents');
      }
    } catch (err) {
      setError('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  }, [folderType, selectedFolder]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (folderType === 'weekly' && !selectedFolder) {
      setError('Please select a weekly folder first');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress(Math.round(((i) / files.length) * 100));

        // Step 1: Request upload URL from API
        const response = await api.post<{ id: number; upload_url: string; r2_key: string }>(
          '/documents/upload',
          {
            filename: file.name,
            mimetype: file.type,
            size: file.size,
            folder_type: folderType,
            weekly_folder_id: folderType === 'weekly' ? selectedFolder?.id : undefined,
          }
        );

        if (!response.success || !response.data) {
          setError(response.error || 'Failed to initiate upload');
          continue;
        }

        // Step 2: Upload the file to the upload URL
        setUploadProgress(Math.round(((i + 0.3) / files.length) * 100));

        const uploadUrl = response.data.upload_url;
        const fullUploadUrl = uploadUrl.startsWith('/')
          ? `${import.meta.env.VITE_API_URL || ''}${uploadUrl}`
          : uploadUrl;

        const uploadResponse = await fetch(fullUploadUrl, {
          method: 'POST',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => ({}));
          setError(errorData.error || 'Failed to upload file to storage');
          continue;
        }

        setUploadProgress(Math.round(((i + 0.7) / files.length) * 100));

        // Step 3: Confirm the upload and trigger processing
        const confirmResponse = await api.post(`/documents/${response.data.id}/confirm`);
        if (!confirmResponse.success) {
          setError(confirmResponse.error || 'Failed to confirm upload');
          continue;
        }

        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }

      await loadDocuments();
    } catch (err) {
      setError('Upload failed');
      console.error('Upload error:', err);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      e.target.value = '';
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await api.delete(`/documents/${id}`);
      if (response.success) {
        setDocuments((prev) => prev.filter((d) => d.id !== id));
      } else {
        setError(response.error || 'Failed to delete document');
      }
    } catch (err) {
      setError('Failed to delete document');
    }
  };

  const handleReprocess = async (id: number) => {
    try {
      const response = await api.post(`/documents/${id}/reprocess`);
      if (response.success) {
        await loadDocuments();
      } else {
        setError(response.error || 'Failed to reprocess document');
      }
    } catch (err) {
      setError('Failed to reprocess document');
    }
  };

  // Open the page upload modal for a needs_review document
  const openPageUpload = async (doc: Document) => {
    setPageUploadDoc(doc);
    setError(null);

    try {
      const response = await api.get<{ pages: ChildPage[] }>(`/documents/${doc.id}/pages`);
      if (response.success && response.data) {
        setChildPages(response.data.pages || []);
      }
    } catch (err) {
      console.error('Failed to load child pages:', err);
      setChildPages([]);
    }
  };

  const closePageUpload = () => {
    setPageUploadDoc(null);
    setChildPages([]);
  };

  // Handle uploading page images
  const handlePageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !pageUploadDoc) return;

    setIsUploadingPages(true);
    setPageUploadProgress(0);
    setError(null);

    try {
      // Upload each file as a page
      const startPageNumber = childPages.length + 1;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const pageNumber = startPageNumber + i;
        setPageUploadProgress(Math.round((i / files.length) * 100));

        // Step 1: Request page upload URL
        const response = await api.post<{ id: number; upload_url: string; page_number: number }>(
          `/documents/${pageUploadDoc.id}/pages`,
          {
            filename: file.name,
            mimetype: file.type,
            size: file.size,
            page_number: pageNumber,
          }
        );

        if (!response.success || !response.data) {
          setError(response.error || `Failed to initiate page ${pageNumber} upload`);
          continue;
        }

        // Step 2: Upload the file
        setPageUploadProgress(Math.round(((i + 0.3) / files.length) * 100));

        const uploadUrl = response.data.upload_url;
        const fullUploadUrl = uploadUrl.startsWith('/')
          ? `${import.meta.env.VITE_API_URL || ''}${uploadUrl}`
          : uploadUrl;

        const uploadResponse = await fetch(fullUploadUrl, {
          method: 'POST',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        if (!uploadResponse.ok) {
          setError(`Failed to upload page ${pageNumber}`);
          continue;
        }

        setPageUploadProgress(Math.round(((i + 0.7) / files.length) * 100));

        // Step 3: Confirm and trigger processing
        const confirmResponse = await api.post(
          `/documents/${pageUploadDoc.id}/pages/${response.data.id}/confirm`
        );
        if (!confirmResponse.success) {
          setError(confirmResponse.error || `Failed to confirm page ${pageNumber}`);
          continue;
        }

        setPageUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }

      // Reload child pages
      const pagesResponse = await api.get<{ pages: ChildPage[] }>(
        `/documents/${pageUploadDoc.id}/pages`
      );
      if (pagesResponse.success && pagesResponse.data) {
        setChildPages(pagesResponse.data.pages || []);
      }
    } catch (err) {
      setError('Page upload failed');
      console.error('Page upload error:', err);
    } finally {
      setIsUploadingPages(false);
      setPageUploadProgress(0);
      e.target.value = '';
    }
  };

  // Consolidate all pages into the parent document
  const handleConsolidate = async () => {
    if (!pageUploadDoc) return;

    const completedPages = childPages.filter((p) => p.status === 'completed');
    if (completedPages.length === 0) {
      setError('No pages have been processed yet. Please wait for processing to complete.');
      return;
    }

    setIsConsolidating(true);
    setError(null);

    try {
      const response = await api.post<{ pages_processed: number; tags: string[] }>(
        `/documents/${pageUploadDoc.id}/pages/consolidate`
      );

      if (response.success) {
        closePageUpload();
        await loadDocuments();
      } else {
        setError(response.error || 'Failed to consolidate pages');
      }
    } catch (err) {
      setError('Failed to consolidate pages');
    } finally {
      setIsConsolidating(false);
    }
  };

  // Refresh page status
  const refreshPages = async () => {
    if (!pageUploadDoc) return;

    try {
      const response = await api.get<{ pages: ChildPage[] }>(
        `/documents/${pageUploadDoc.id}/pages`
      );
      if (response.success && response.data) {
        setChildPages(response.data.pages || []);
      }
    } catch (err) {
      console.error('Failed to refresh pages:', err);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: Document['status']) => {
    const statusMap: Record<string, string> = {
      completed: 'success',
      processing: 'warning',
      error: 'error',
      pending: 'info',
      needs_review: 'warning',
    };
    return `badge badge-${statusMap[status] || 'info'}`;
  };

  const getStatusLabel = (status: Document['status']) => {
    if (status === 'needs_review') return 'needs review';
    return status;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>Documents</h1>
          <p className={styles.subtitle}>
            {folderType === 'weekly'
              ? `Documents for ${selectedFolder?.title || 'selected week'}`
              : 'Permanent reference documents'}
          </p>
        </div>

        {isAdmin && (
          <div className={styles.uploadSection}>
            <input
              type="file"
              id="file-upload"
              className={styles.fileInput}
              onChange={handleFileUpload}
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.txt,.csv,.json,.docx,.xlsx,.pptx"
              disabled={isUploading}
            />
            <label htmlFor="file-upload" className="btn btn-primary">
              {isUploading ? (
                <>
                  <span className="spinner" />
                  Uploading {uploadProgress}%
                </>
              ) : (
                'Upload Documents'
              )}
            </label>
          </div>
        )}
      </div>

      {/* Folder Type Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${folderType === 'weekly' ? styles.tabActive : ''}`}
          onClick={() => setFolderType('weekly')}
        >
          Weekly Documents
        </button>
        <button
          className={`${styles.tab} ${folderType === 'permanent' ? styles.tabActive : ''}`}
          onClick={() => setFolderType('permanent')}
        >
          Permanent References
        </button>
      </div>

      {error && (
        <div className={styles.error}>{error}</div>
      )}

      {/* Documents List */}
      {isLoading ? (
        <div className={styles.loading}>
          <div className="spinner" />
          <span>Loading documents...</span>
        </div>
      ) : documents.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>📄</span>
          <p>No documents found</p>
          {isAdmin && (
            <p className={styles.emptyHint}>
              Upload documents to get started
            </p>
          )}
        </div>
      ) : (
        <div className={styles.documentList}>
          {documents.map((doc) => (
            <div key={doc.id} className={styles.documentCard}>
              <div className={styles.docInfo}>
                <div className={styles.docName}>{doc.original_name}</div>
                <div className={styles.docMeta}>
                  <span>{formatFileSize(doc.size)}</span>
                  <span>·</span>
                  <span>{formatDate(doc.created_at)}</span>
                </div>
                {doc.tags.length > 0 && (
                  <div className={styles.docTags}>
                    {doc.tags.slice(0, 5).map((tag, i) => (
                      <span key={i} className={styles.tag}>{tag}</span>
                    ))}
                    {doc.tags.length > 5 && (
                      <span className={styles.tagMore}>+{doc.tags.length - 5}</span>
                    )}
                  </div>
                )}
                {doc.error_message && (
                  <div className={styles.docError}>{doc.error_message}</div>
                )}
              </div>
              <div className={styles.docActions}>
                <span className={getStatusBadge(doc.status)}>{getStatusLabel(doc.status)}</span>
                {isAdmin && (
                  <div className={styles.actionButtons}>
                    {doc.status === 'needs_review' && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => openPageUpload(doc)}
                      >
                        Upload Pages
                      </button>
                    )}
                    {doc.status === 'error' && (
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => handleReprocess(doc.id)}
                      >
                        Retry
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(doc.id)}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Page Upload Modal */}
      {pageUploadDoc && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>Upload Pages</h2>
              <button className={styles.modalClose} onClick={closePageUpload}>
                &times;
              </button>
            </div>

            <div className={styles.modalBody}>
              <p className={styles.modalDescription}>
                Upload individual page images for <strong>{pageUploadDoc.original_name}</strong>.
                Each image will be processed separately and the content will be combined.
              </p>

              {error && <div className={styles.error}>{error}</div>}

              {/* Upload Section */}
              <div className={styles.pageUploadSection}>
                <input
                  type="file"
                  id="page-upload"
                  className={styles.fileInput}
                  onChange={handlePageUpload}
                  multiple
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  disabled={isUploadingPages}
                />
                <label htmlFor="page-upload" className="btn btn-primary">
                  {isUploadingPages ? (
                    <>
                      <span className="spinner" />
                      Uploading {pageUploadProgress}%
                    </>
                  ) : (
                    'Select Page Images'
                  )}
                </label>
                <span className={styles.uploadHint}>
                  Supported: JPEG, PNG, GIF, WebP
                </span>
              </div>

              {/* Child Pages List */}
              {childPages.length > 0 && (
                <div className={styles.pagesList}>
                  <div className={styles.pagesHeader}>
                    <h3>Uploaded Pages ({childPages.length})</h3>
                    <button className="btn btn-sm btn-outline" onClick={refreshPages}>
                      Refresh Status
                    </button>
                  </div>

                  <div className={styles.pagesGrid}>
                    {childPages
                      .sort((a, b) => (a.page_number || 0) - (b.page_number || 0))
                      .map((page) => (
                        <div key={page.id} className={styles.pageCard}>
                          <div className={styles.pageNumber}>Page {page.page_number}</div>
                          <span className={getStatusBadge(page.status)}>
                            {getStatusLabel(page.status)}
                          </span>
                        </div>
                      ))}
                  </div>

                  {/* Progress Summary */}
                  <div className={styles.pagesSummary}>
                    <span>
                      {childPages.filter((p) => p.status === 'completed').length} of{' '}
                      {childPages.length} pages processed
                    </span>
                    {childPages.some((p) => p.status === 'error') && (
                      <span className={styles.pagesError}>
                        {childPages.filter((p) => p.status === 'error').length} errors
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button className="btn btn-outline" onClick={closePageUpload}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConsolidate}
                disabled={
                  isConsolidating ||
                  childPages.filter((p) => p.status === 'completed').length === 0
                }
              >
                {isConsolidating ? (
                  <>
                    <span className="spinner" />
                    Consolidating...
                  </>
                ) : (
                  'Consolidate Pages'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
